import { askCopilotTurn } from './stream'
import { isPendingTurnRefusal, newCopilotSession } from './session'
import { turnMetadata } from './turnMetadata'
import { emptyTurnState, reduceTurnEvent } from './turnReducer'

/**
 * A real copilot turn, in the shape the Copilot screen already renders.
 *
 * The screen was built to take one and the page above this does not change.
 *
 * ── Why the whole answer arrives at once, and why that is right ───────────
 * The engine emits its answer as a single block that grows, and every claim in it
 * is checked against the records it read before any of it is emitted. Streaming raw
 * model output would put a sentence on screen and then retract it. So
 * `useStreamedBlocks` keeps revealing on its timer, which is an honest presentation
 * of an answer that genuinely did arrive complete.
 *
 * ── The blocks are not decoration ─────────────────────────────────────────
 * `sources` becomes an evidence block because it is the difference between an
 * answer and an assertion: the service strips any claim it cannot tie to a record,
 * so the list underneath is the whole reason the text above it is worth believing.
 *
 * ── One accumulator, shared with the dock ─────────────────────────────────
 * The folding of frames onto a turn is `turnReducer`, the same module the dock
 * drives. This file used to keep its own switch over the same stream, and two
 * hand-written accumulators over one protocol is how they drift — which is exactly
 * what happened: both were still reading an event vocabulary the server stopped
 * sending, and neither could report that it had.
 */

/**
 * Evidence rows are labelled through `analysis.evidence.<key>`, a bundle written
 * against the engine's own singular nouns. The server's `sources` use the product's
 * plural module names. One mapping here rather than a rename of the bundle, which is
 * shared with the anomaly screen.
 *
 * Everything unmapped falls back to the generic label. `module` legitimately mixes
 * addressable records with reasoning artefacts — `statistic`, `relationship`,
 * `diagnosis` — and a key with no string prints itself at the reader, which is how
 * `relationship` would end up on screen as though it were a word.
 */
const EVIDENCE_KEY_BY_MODULE = {
  anomalies: 'anomaly',
  assets: 'asset',
  anomaly: 'anomaly',
  asset: 'asset',
  rca: 'rca',
  statistic: 'statistic',
  relationship: 'relationship',
}

/**
 * Steps with a sentence to show for them.
 *
 * The engine's step ids are `tool:<name>` for a tool call and `rca:<uuid>` for an
 * analysis. The tool names are the engine's function names, and the standing rule
 * in this file is that an untranslatable step is DROPPED rather than printed: a
 * missing step is a quieter failure than a raw identifier at the user.
 *
 * Mirrors `copilot/tools/registry.py`. `run_rca` was here and is gone — that tool
 * was deleted, and an analysis is a turn's intent now rather than a call the model
 * chooses to make.
 */
const KNOWN_STEPS = new Set([
  'resolve_entity',
  'get_anomaly',
  'find_anomalies',
  'series_stats',
  'get_rca',
  'search_documents',
  'get_sensor_limits',
  'find_related_equipment',
  'diagnose',
  'get_shift_context',
  'search_logbook',
  /* Not a tool — the analysis step, which announces itself once and is closed by
     the turn ending rather than by a duration. */
  'rca',
])

/** `tool:get_anomaly` → `get_anomaly`; `rca:<uuid>` → `rca`, one step whatever it analysed. */
function stepKey(id = '') {
  if (id.startsWith('rca:')) return 'rca'
  return id.startsWith('tool:') ? id.slice(5) : id
}

export async function askCopilotLive({ question, sessionId, signal }) {
  let state = { ...emptyTurnState }
  const onEvent = (event) => {
    const change = reduceTurnEvent(state, event)
    if (change) state = { ...state, ...change }
  }

  let session = sessionId ?? (await newCopilotSession(signal))?.id ?? null
  const metadata = turnMetadata({ route: 'copilot' })

  try {
    await askCopilotTurn({ sessionId: session, message: question, metadata, signal, onEvent })
  } catch (error) {
    /* The session's last question was never answered, and never will be — a failed
       turn leaves it that way permanently and nothing can clear it. The screen holds
       one thread and has no transcript to protect, so it simply moves to a new
       conversation and asks again. Once. */
    if (!isPendingTurnRefusal(error)) throw error
    session = (await newCopilotSession(signal))?.id ?? null
    state = { ...emptyTurnState }
    await askCopilotTurn({ sessionId: session, message: question, metadata, signal, onEvent })
  }

  const blocks = []
  if (state.text) blocks.push({ type: 'text', text: state.text })
  if (state.sources.length) {
    blocks.push({
      type: 'evidence',
      rows: state.sources.map((source) => ({
        key: EVIDENCE_KEY_BY_MODULE[source.module] ?? 'evidence',
        value: source.title || source.module_id,
      })),
    })
  }

  return {
    /* `matched: false` makes the screen print its own "I could not answer" copy.
       A live turn that produced text has answered — even "the records show nothing"
       is an answer, and the most important one this system gives. */
    matched: !state.error && Boolean(state.text),
    blocks,
    reasoning: state.stages.map((stage) => stepKey(stage.id)).filter((key) => KNOWN_STEPS.has(key)),
    /* Structurally empty. The engine produces follow-up choices and the chunk
       protocol has no field to carry them, so it counts them and drops them. The key
       stays because the screen destructures it; the row above it is gone. */
    suggestions: [],
    sessionId: session,
  }
}
